import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaTimes, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import './Toast.css';

const ICONS = {
  success: <FaCheck />,
  error:   <FaTimes />,
  warning: <FaExclamationTriangle />,
  info:    <FaInfoCircle />,
};

const TITLES = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Information',
};

/** Duration in ms. Errors persist (null = no auto-dismiss). */
const DURATIONS = {
  success: 5000,
  warning: 7000,
  info: 5000,
  error: null,
};

/**
 * Single toast item.
 *
 * @param {{ toast: import('../../context/ToastContext').ToastItem, onDismiss: (id: string) => void }} props
 */
export function ToastItem({ toast, onDismiss }) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);
  const duration = toast.duration !== undefined ? toast.duration : DURATIONS[toast.type];

  const dismiss = () => {
    setExiting(true);
    // Wait for exit animation before removing from DOM
    setTimeout(() => onDismiss(toast.id), 220);
  };

  useEffect(() => {
    if (duration === null) return;
    timerRef.current = setTimeout(dismiss, duration);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`toast toast-${toast.type}${exiting ? ' toast-exiting' : ''}`}
      style={{ '--toast-duration': duration ? `${duration}ms` : undefined }}
    >
      <span className="toast-icon" aria-hidden="true">
        {ICONS[toast.type]}
      </span>

      <div className="toast-body">
        {toast.title && <p className="toast-title">{toast.title}</p>}
        <p className="toast-message">{toast.message}</p>
      </div>

      <button
        className="toast-close"
        onClick={dismiss}
        aria-label={`Dismiss ${TITLES[toast.type]} notification`}
      >
        ×
      </button>

      {duration !== null && (
        <div className="toast-progress" aria-hidden="true" />
      )}
    </div>
  );
}

/**
 * Toast container — renders all active toasts at the top-right of the viewport.
 *
 * @param {{ toasts: import('../../context/ToastContext').ToastItem[], onDismiss: (id: string) => void }} props
 */
export function ToastContainer({ toasts, onDismiss }) {
  return (
    <div
      className="toast-container"
      aria-label="Notifications"
      role="region"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
